import { AlertTriangle, ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import type { ReactNode } from 'react';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Sparkline } from './Sparkline';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tooltip } from '@/components/ui/Tooltip';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

export interface KPICardProps {
  label: string;
  value: ReactNode;
  /** Percent delta vs previous period (e.g. 0.124 = +12.4%). */
  deltaPct?: number | null;
  /** Higher is better → green on positive; set false for inverse metrics like cancel %. */
  goodWhenUp?: boolean;
  /** Sparkline series, low-fidelity trend. */
  trend?: { value: number }[];
  hint?: string;
  loading?: boolean;
  /**
   * The query behind this tile failed.
   *
   * Worth a dedicated prop rather than leaving it to callers: nearly every KPI
   * strip formats a `?? 0` fallback, so a failed request renders a confident
   * "0" — indistinguishable from a genuinely zero metric, and the one number
   * you must never invent on an executive dashboard.
   */
  error?: boolean;
  onRetry?: () => void;
  className?: string;
  /** Optional element rendered in the header (e.g. a "Why?" popover trigger). */
  action?: ReactNode;
}

export function KPICard({
  label,
  value,
  deltaPct,
  goodWhenUp = true,
  trend,
  hint,
  loading,
  error,
  onRetry,
  className,
  action,
}: KPICardProps) {
  return (
    <Card className={cn('relative overflow-hidden', className)}>
      <CardHeader>
        <CardTitle>{label}</CardTitle>
        <div className="ml-auto flex items-center gap-1.5">
          {hint ? (
            <Tooltip content={hint}>
              <span className="cursor-default text-[10px] font-medium text-muted-foreground/70">i</span>
            </Tooltip>
          ) : null}
          {action}
        </div>
      </CardHeader>
      <CardBody>
        {loading ? (
          <Skeleton className="h-8 w-24" />
        ) : error ? (
          <p className="font-display text-2xl font-bold tracking-tight text-muted-foreground sm:text-3xl">
            —
          </p>
        ) : (
          <p className="font-display text-2xl font-bold tracking-tight kpi-num sm:text-3xl">{value}</p>
        )}
        <div className="mt-2 flex items-center justify-between gap-2">
          {error ? (
            <span className="inline-flex items-center gap-1 text-xs text-danger">
              <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
              Unavailable
              {onRetry ? (
                <button
                  type="button"
                  onClick={onRetry}
                  className="press rounded underline decoration-dotted focus-ring"
                >
                  Retry
                </button>
              ) : null}
            </span>
          ) : (
            <DeltaPill deltaPct={deltaPct ?? null} goodWhenUp={goodWhenUp} loading={loading} />
          )}
          {!error && trend && trend.length > 1 ? (
            <div className="ml-auto w-24 sm:w-32">
              <Sparkline data={trend} tone={deltaTone(deltaPct ?? null, goodWhenUp)} />
            </div>
          ) : null}
        </div>
      </CardBody>
    </Card>
  );
}

function deltaTone(deltaPct: number | null, goodWhenUp: boolean): 'success' | 'danger' | 'primary' {
  if (deltaPct == null || Number.isNaN(deltaPct)) return 'primary';
  const positive = deltaPct > 0;
  const isGood = goodWhenUp ? positive : !positive;
  if (deltaPct === 0) return 'primary';
  return isGood ? 'success' : 'danger';
}

function DeltaPill({
  deltaPct,
  goodWhenUp,
  loading,
}: {
  deltaPct: number | null;
  goodWhenUp: boolean;
  loading?: boolean;
}) {
  if (loading) return <Skeleton className="h-4 w-14" />;
  if (deltaPct == null || Number.isNaN(deltaPct)) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const tone = deltaTone(deltaPct, goodWhenUp);
  const Icon = deltaPct > 0 ? ArrowUpRight : deltaPct < 0 ? ArrowDownRight : Minus;
  const color =
    tone === 'success'
      ? 'text-success'
      : tone === 'danger'
        ? 'text-danger'
        : 'text-muted-foreground';
  return (
    <span className={cn('inline-flex items-center gap-0.5 text-xs font-medium', color)}>
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {fmt.delta(deltaPct, { unit: 'pct' })}
    </span>
  );
}
